import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { logger } from "../utils/logger";

let initialized = false;

const fallbackDsn =
  "https://28332d7873108e636b620c5caefb7dbc@o569482.ingest.us.sentry.io/5715263";

/**
 * Initialize Sentry for the browser runtime.
 * Safe to call multiple times; subsequent calls will be ignored.
 */
export function initSentry() {
  if (initialized || import.meta.env.SSR) {
    return;
  }

  const envDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const isProd = import.meta.env.MODE === "production";
  const dsn = envDsn ?? (isProd ? fallbackDsn : undefined);

  if (!dsn) {
    logger.warn("[Sentry] DSN is not configured, skipping initialization");
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      integrations: [
        Sentry.reactRouterV7BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.browserTracingIntegration(),
      ],
      tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
    });

    initialized = true;
    logger.info("[Sentry] Initialized");
  } catch (error) {
    logger.error("[Sentry] Failed to initialize", error);
  }
}

/**
 * Capture an exception when Sentry is ready.
 */
export function captureException(error: unknown, context?: {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}) {
  if (!initialized) {
    return;
  }

  Sentry.captureException(error, context);
}

/** Add bounded lifecycle context to the next captured client error. */
export function addSentryBreadcrumb(input: {
  category: string;
  message: string;
  data?: Record<string, unknown>;
}) {
  if (!initialized) return;

  Sentry.addBreadcrumb({
    ...input,
    level: 'info',
  });
}
